import { GridColDef } from '@mui/x-data-grid-pro'
import { useMemo } from 'react'
import { ArrayElement } from '../../../../utils/types'
import { GetApiKeysQuery } from '../../../../generated/graphql'
import { Chip, Stack, Typography } from '@mui/material'
import { useLocalizedDate } from '../../../../hooks/localized-date'
import { useTranslation } from 'react-i18next'
import { RelativeDateRenderer } from '../../../../components/data-grid/utils/relative-date-renderer'

export type Row = ArrayElement<GetApiKeysQuery['apiKeys']['items']>

export const useApiKeyColumns = () => {
  const { formatRelativeDate, formatDate } = useLocalizedDate()
  const { t } = useTranslation(['api_keys'])

  const dataGridColumns = useMemo<GridColDef<Row>[]>(
    () => [
      {
        field: 'username',
        headerName: t('api_keys:id'),
        minWidth: 100,
      },
      {
        field: 'name',
        headerName: t('api_keys:name'),
        minWidth: 150,
      },
      {
        field: 'enabled',
        headerName: t('api_keys:status'),
        minWidth: 100,
        renderCell: (params) => {
          const status = params.value as string
          return (
            <Chip
              label={status ? t('api_keys:enabled') : t('api_keys:disabled')}
              variant={status ? 'outlined' : 'filled'}
              color={status ? 'green' : 'red'}
            />
          )
        },
      },
      {
        field: 'last_used',
        headerName: t('api_keys:last_used'),
        minWidth: 200,
        renderCell: (params) => (
          <RelativeDateRenderer
            {...params}
            formatDate={formatDate}
            formatRelativeDate={formatRelativeDate}
          />
        ),
      },
      {
        field: 'type',
        headerName: t('api_keys:type'),
        minWidth: 100,
        valueFormatter: (value: string) => {
          return t(`api_keys:type_${value}`) || value
        },
      },
      {
        field: 'ip_restrictions',
        headerName: t('api_keys:ip_restrictions'),
        minWidth: 200,
        flex: 1,
        renderCell: (params) => {
          const ipRestrictions = (params.value || []) as Row['ip_restrictions']
          const displayLimit = 2
          const extraCount = ipRestrictions.length - displayLimit

          return (
            <Stack
              direction="row"
              flexWrap="wrap"
              alignItems="center"
              sx={{
                py: 0.5,
                width: '100%',
                overflow: 'hidden',

                height: '100%',
                gap: 0.5,
              }}
            >
              {ipRestrictions.slice(0, displayLimit).map((ipAddress) => (
                <Chip
                  key={ipAddress}
                  label={ipAddress}
                  variant="outlined"
                  size="small"
                />
              ))}
              {extraCount > 0 && (
                <Typography variant="caption">{`+${extraCount}`}</Typography>
              )}
            </Stack>
          )
        },
      },
    ],
    [formatDate, formatRelativeDate, t],
  )

  return dataGridColumns
}
